import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs } from '../../../api/blogApi';
import { ArrowRight } from 'lucide-react';
import { BlogShimmer } from '../../../components/ShimmerEffect';

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await getBlogs({ status: 'Published', limit: 3 });
        if (response.success && response.data) {
          setBlogs(response.data);
        }
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  return (
    <section className="py-16 bg-white dark:bg-gray-950">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <div className="h-[1px] w-12 bg-amber-500" />
              <span className="text-amber-600 dark:text-amber-400 font-bold tracking-widest text-xs uppercase">
                Learn More
              </span>
            </div>
            <h2 className="text-4xl font-serif font-bold text-gray-900 dark:text-gray-100 mb-2">
              From the blog
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Buying guides, comparisons, and tips for refurbished buyers.
            </p>
          </div>
          <Link
            to="/blogs"
            className="mt-6 md:mt-0 inline-flex items-center justify-center px-6 py-2 border border-gray-200 dark:border-gray-700 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            All Articles <ArrowRight size={16} className="ml-2 text-gray-400 dark:text-gray-500" />
          </Link>
        </div>

        {/* Cards Grid */}
        {loading ? (
          <BlogShimmer />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.length > 0 ? (
              blogs.map((blog) => (
              <div key={blog._id} className="group cursor-pointer">
                <Link to={`/blog/${blog.slug}`}>
                  {/* Image Card */}
                  <div className="relative w-full aspect-video rounded-[2rem] overflow-hidden mb-4 shadow-sm group-hover:shadow-xl transition-shadow duration-300">
                    <img
                      src={blog.featuredImage}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Badge */}
                    <div className="absolute bottom-16 left-6">
                      <span className="bg-white/90 backdrop-blur-sm text-gray-800 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                        {blog.category}
                      </span>
                    </div>

                    {/* Title Overlay */}
                    <div className="absolute bottom-4 left-6 right-6">
                      <h3 className="text-white font-bold text-lg leading-snug line-clamp-2">
                        {blog.title}
                      </h3>
                    </div>
                  </div>

                  {/* Meta & Excerpt */}
                  <div className="px-2">
                    <div className="flex items-center text-[10px] font-bold uppercase tracking-widest mb-3 gap-2">
                      <span className="text-amber-600 dark:text-amber-400">{blog.author || 'Bright Laptop'}</span>
                      <span className="w-1 h-1 rounded-full bg-amber-400" />
                      <span className="text-amber-500 dark:text-amber-400">
                        {blog.publishedAt
                          ? new Date(blog.publishedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                          : 'MAR 2025'}
                      </span>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">
                      {blog.excerpt}
                    </p>
                  </div>
                </Link>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-12 text-gray-500 dark:text-gray-400">
              No articles published yet.
            </div>
          )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
